import { displayDate } from '../../api/adapter';

/** The spread's own header row, above both pages.
 *
 *  Mirrors the wide two-day frame in the design handoff: `DAILY LOG ·
 *  TWO-DAY SPREAD` on the left, then the spread's date range with its keyboard
 *  hints and the sync state on the right.
 *
 *  This chrome belongs to the *spread*, not to a page, and putting it in a page
 *  was the bug it fixes. The breadcrumb was printed twice, once per page; the
 *  badge and the day pager were printed on the active page only, which made that
 *  page's header taller than its twin and knocked every rule and section below it
 *  out of line with the day beside it. One row above both pages has neither
 *  problem, and it is what the frame draws.
 */
export function SpreadBar({
  label,
  onToggleDays,
  from,
  to,
  queued,
  offline,
  onPrevious,
  onNext,
}: {
  /** `TWO-DAY SPREAD`, or `FOCUSED` when one day is pinned. */
  label: string;
  /** Switch between two days and one. The bar stays put in both modes: a toggle
   *  that disappears along with the mode it switches out of is a trap.
   *
   *  Null where there is no choice to make — when the measured fallback has
   *  already dropped a column because a calendar title would clip, tapping this
   *  would change a preference and nothing on screen, which reads as broken. */
  onToggleDays: (() => void) | null;
  /** The spread's first and last day, as journal dates. */
  from: string;
  to: string;
  queued: number;
  offline: boolean;
  onPrevious: () => void;
  onNext: () => void;
}) {
  const alert = offline || queued > 0;
  return (
    <div
      style={{
        minHeight: 44,
        flex: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--bujo-space-4)',
        padding: '0 22px 5px',
        font: 'var(--bujo-fw-medium) var(--bujo-fs-micro)/var(--bujo-lh-tight) var(--bujo-font-mono)',
        color: 'var(--bujo-text-meta)',
        flexWrap: 'wrap',
      }}
    >
      <span style={{ letterSpacing: 'var(--bujo-ls-badge)', whiteSpace: 'nowrap' }}>
        DAILY LOG{' '}
        {/* The mode's own name is the control that changes it — the label was
            already saying which mode this is, and a separate switch beside it
            would say the same thing twice. */}
        {onToggleDays ? (
          <button
            type="button"
            onClick={onToggleDays}
            aria-label={`${label} — switch to ${label === 'FOCUSED' ? 'two days' : 'one day'}`}
            style={{
              minHeight: 'var(--bujo-row-min)',
              padding: '0 var(--bujo-space-1)',
              background: 'none',
              border: 0,
              cursor: 'pointer',
              color: 'var(--bujo-accent)',
              font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
              letterSpacing: 'var(--bujo-ls-label)',
            }}
          >
            · {label}
          </button>
        ) : (
          <span style={{ color: 'var(--bujo-accent)', font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)', letterSpacing: 'var(--bujo-ls-label)' }}>
            · {label}
          </span>
        )}
      </span>

      <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--bujo-space-3)' }}>
        <nav
          aria-label="Change day"
          style={{ display: 'flex', alignItems: 'center', gap: 'var(--bujo-space-2)' }}
        >
          {/* The hints are real bindings, not decoration — `h` and `l` page the
              spread, the same keys the terminal client uses. Rendering them
              without wiring them would be a lie printed on the chrome. */}
          <span aria-hidden="true" style={hint}>
            h
          </span>
          <button type="button" onClick={onPrevious} style={step} aria-label="Previous day">
            ◀
          </button>
          <span style={{ color: 'var(--bujo-text-primary)', whiteSpace: 'nowrap' }}>
            {dateRange(from, to)}
          </span>
          <button type="button" onClick={onNext} style={step} aria-label="Next day">
            ▶
          </button>
          <span aria-hidden="true" style={hint}>
            l
          </span>
        </nav>
        <span
          style={{
            color: alert ? 'var(--bujo-state-queued)' : 'var(--bujo-text-meta)',
            whiteSpace: 'nowrap',
          }}
        >
          ⌁ {offline ? 'OFFLINE' : `QUEUED ${queued}`}
        </span>
      </span>
    </div>
  );
}

/** `01 – 02 AUG 2026`, collapsing whatever the two days share.
 *
 *  Written out in full it would be `01 AUG 2026 – 02 AUG 2026`, which says the
 *  month and the year twice for the ordinary case of two days in one month.
 */
export function dateRange(from: string, to: string): string {
  if (from === to) return displayDate(to);
  const [fy, fm] = from.split('-');
  const [ty, tm] = to.split('-');
  const left = displayDate(from);
  const right = displayDate(to);
  if (fy === ty && fm === tm) return `${left.slice(0, 2)} – ${right}`;
  if (fy === ty) return `${left.slice(0, 6)} – ${right}`;
  return `${left} – ${right}`;
}

const step = {
  minHeight: 'var(--bujo-row-min)',
  minWidth: 'var(--bujo-row-min)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'none',
  border: 0,
  font: 'inherit',
  color: 'var(--bujo-accent)',
  cursor: 'pointer',
};

const hint = {
  font: 'var(--bujo-fw-regular) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  color: 'var(--bujo-text-dim)',
};
